import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  GraduationCap, Video, ShieldCheck, CalendarCheck, Star, ArrowRight, Check,
  Brain, Users, Sparkles, BookOpen, Newspaper, Atom, Microscope,
  Mail, Facebook, Instagram, ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, delay: d },
});

const NAV_LINKS = [
  ["Subjects", "#subjects"], ["About", "#about"], ["Courses", "#courses"],
  ["Prices", "#prices"], ["Reviews", "#reviews"], ["Contact", "#contact"],
];

const Nav = () => (
  <header className="fixed top-0 inset-x-0 z-50">
    <div className="max-w-7xl mx-auto px-6 mt-4">
      <nav className="flex items-center justify-between rounded-full border border-white/50 bg-white/70 backdrop-blur-xl px-6 py-3 shadow-sm">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
          <img src="/logo_mark.png" alt="Up Smart Learning logo" className="h-10 w-10 object-contain" />
          <span className="font-display text-xl font-bold text-[#595C2B]">Up Smart Learning</span>
        </Link>
        <div className="hidden lg:flex items-center gap-7 text-sm font-medium text-[#3a3f2e]">
          {NAV_LINKS.map(([t, h]) => <a key={t} href={h} className="hover:text-[#595C2B]">{t}</a>)}
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="nav-login-btn">
            <Button variant="ghost" className="rounded-full text-[#595C2B] hover:bg-[#595C2B]/10">Log in</Button>
          </Link>
          <Link to="/login?mode=register" data-testid="nav-register-btn">
            <Button className="rounded-full bg-[#BA8A30] hover:bg-[#9C7328] text-[#2A1E0E] font-semibold btn-lift">Book a lesson</Button>
          </Link>
        </div>
      </nav>
    </div>
  </header>
);

const subjects = [
  { t: "Mathematics", l: "GCSE & A-Level", icon: BookOpen, span: "md:col-span-4" },
  { t: "Sciences", l: "Physics · Chemistry · Biology · Psychology", icon: Atom, span: "md:col-span-8" },
  { t: "English", l: "Language & Literature", icon: BookOpen, span: "md:col-span-7" },
  { t: "Humanities", l: "Geography · Business · Religious Studies", icon: GraduationCap, span: "md:col-span-5" },
];

const packages = [
  { t: "8-Hour Package", gcse: "£175", alevel: "£195", free: "1 free lesson", pts: ["8 hours of tuition", "Flexible scheduling", "Ideal for a single topic"] },
  { t: "16-Hour Package", gcse: "£344", alevel: "£392", free: "2 free lessons", featured: true, pts: ["16 hours of tuition", "Best value for a full term", "Progress reviews"] },
  { t: "24-Hour Package", gcse: "£525", alevel: "£588", free: "3 free lessons", pts: ["24 hours of tuition", "Complete exam preparation", "Priority tutor matching"] },
];

const examBoards = ["AQA", "Edexcel", "OCR", "WJEC", "Eduqas", "CCEA"];

const methods = [
  { icon: Brain, t: "Scientific learning methods", d: "Spaced repetition, retrieval practice and interleaving — techniques proven by cognitive science to make revision stick." },
  { icon: Sparkles, t: "Personalised plans", d: "Every student starts with a diagnostic, then a bespoke plan targeting their weakest exam objectives." },
  { icon: Microscope, t: "Active recall & feedback", d: "Regular low-stakes quizzing and detailed marking against real exam mark schemes." },
];

const reviews = [
  { n: "Sarah", role: "Parent", r: "My daughter went from a grade 5 to a grade 8 in GCSE Maths. The tutor was patient and the Google Meet lessons were so convenient.", stars: 5 },
  { n: "David", role: "Parent", r: "The A-Level Chemistry package was brilliant value. Booking and paying online took two minutes and the reminder emails meant we never missed a session.", stars: 5 },
  { n: "Aisha", role: "Parent", r: "Finally a tutoring service that actually explains the exam technique. My son feels genuinely confident going into his exams.", stars: 5 },
  { n: "Tyler", role: "Student", r: "I felt I have gotten much better at Maths and Science after receiving lessons with Gurpinder at Up Smart Learning. I'm much more confident going into my GCSEs knowing I've received top teaching from a great tutor.", stars: 5 },
  { n: "Avinah", role: "Parent", r: "Gurpinder teaches A-Level Maths and Physics concepts very well; he is a patient and very knowledgeable tutor. My grades have improved significantly, I'd highly recommend. He explains concepts clearly and makes learning feel easy and enjoyable.", stars: 5 },
  { n: "Uzma", role: "Parent", r: "Very useful, flexible and tailored lessons to exactly what I need to focus on at a given point in time. Janisha has a strong base in Maths, English and Science and can explain topics with ease in a way a student can understand, especially those grade 8/9 topics. The lessons are always really interactive and I've really benefited.", stars: 5 },
  { n: "Lohan", role: "Student", r: "Janisha is a great tutor, explains things clearly and in a way that you can understand. Always takes the time to make sure I actually get it before moving on. I've definitely improved since starting sessions.", stars: 5 },
];

function Contact() {
  const EMAIL = "info@upsmartlearning.co.uk";
  return (
    <div className="rounded-2xl border border-[#d8cfb8] bg-white p-8" data-testid="contact-email-card">
      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#BA8A30]">Email us</p>
      <a href={`mailto:${EMAIL}?subject=Free%20meeting%20request`} className="mt-3 flex items-center gap-3 group" data-testid="contact-email-link">
        <span className="h-12 w-12 rounded-xl bg-[#595C2B] flex items-center justify-center shrink-0"><Mail className="h-6 w-6 text-[#BA8A30]" /></span>
        <span className="font-display text-xl sm:text-2xl font-semibold text-[#595C2B] group-hover:underline break-all">{EMAIL}</span>
      </a>
      <p className="text-sm text-[#6a6f58] mt-4">Send us an email to arrange your free meeting and we'll get back to you promptly.</p>
      <a href={`mailto:${EMAIL}?subject=Free%20meeting%20request`}>
        <Button className="mt-5 rounded-full bg-[#BA8A30] hover:bg-[#9C7328] text-[#2A1E0E] font-semibold btn-lift" data-testid="contact-email-btn">
          <Mail className="h-4 w-4 mr-2" /> Email us to book a free meeting
        </Button>
      </a>
    </div>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#EFE8D8] grain">
      <Nav />

      {/* Hero */}
      <section className="relative pt-36 pb-24 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 items-center">
          <motion.div className="md:col-span-6" {...fade(0)}>
            <span className="inline-flex items-center gap-2 rounded-full border border-[#BA8A30]/50 bg-[#BA8A30]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-[#8a6a1c]">
              <ShieldCheck className="h-3.5 w-3.5" /> UK GCSE & A-Level Specialists
            </span>
            <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight leading-[1.05] text-[#595C2B] mt-6">
              Inspiring Excellence<br />Achieving Success
            </h1>
            <p className="text-lg text-[#4a4f3c] mt-6 max-w-lg leading-relaxed">
              One-to-one and small-group online tuition for GCSE and A-Level students, delivered live on Google Meet by top-university students and graduates. Book, pay and track lessons in one place.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Link to="/contact-us" data-testid="hero-book-btn">
                <Button size="lg" className="rounded-full bg-[#BA8A30] hover:bg-[#9C7328] text-[#2A1E0E] font-semibold btn-lift h-12 px-8">
                  Book your free meeting <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href="#courses" data-testid="hero-courses-btn">
                <Button size="lg" variant="outline" className="rounded-full h-12 px-8 border-[#595C2B]/30 text-[#595C2B] hover:bg-white">View courses</Button>
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-10 text-sm text-[#45481F]">
              <div className="flex items-center gap-1.5">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-[#BA8A30] text-[#BA8A30]" />)}
                <span className="ml-1 font-semibold text-[#595C2B]">4.9/5 stars</span>
              </div>
              <span>Trusted by 200+ UK families</span>
            </div>
          </motion.div>

          <motion.div className="md:col-span-6" {...fade(0.15)}>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#595C2B] rounded-[2rem] rotate-2" />
              <img src="https://images.pexels.com/photos/5905712/pexels-photo-5905712.jpeg" alt="Student in an online tutoring lesson" className="relative rounded-[1.75rem] object-cover w-full h-[460px] shadow-2xl" />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl border border-[#E7E2D3] p-4 flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-[#595C2B] flex items-center justify-center"><Video className="h-5 w-5 text-[#BA8A30]" /></div>
                <div><p className="text-sm font-bold text-[#595C2B]">Live on Google Meet</p><p className="text-xs text-[#6a6f58]">Auto-scheduled lessons</p></div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Subjects */}
      <section id="subjects" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fade()} className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#BA8A30]">What we teach</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#595C2B] mt-3 tracking-tight">Focused on the subjects that matter most</h2>
          </motion.div>
          <div className="grid md:grid-cols-12 gap-6 mt-12">
            {subjects.map((s, i) => (
              <motion.div key={s.t} {...fade(i * 0.08)} className={`${s.span} rounded-2xl border border-[#a5a36b] bg-[#B3B17D] p-8 hover:shadow-md btn-lift`}>
                <s.icon className="h-7 w-7 text-[#2A1E0E]" />
                <h3 className="font-display text-2xl font-semibold text-[#2A1E0E] mt-4">{s.t}</h3>
                <p className="text-[#45481F] mt-1">{s.l}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-24 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-14 items-center">
          <motion.img {...fade()} src="https://images.pexels.com/photos/5306449/pexels-photo-5306449.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Two students studying together at home" className="rounded-2xl object-cover h-[420px] w-full shadow-lg" />
          <motion.div {...fade(0.1)}>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#BA8A30]">About us</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#595C2B] mt-3 tracking-tight">Who we are &amp; why choose us</h2>
            <p className="text-[#4a4f3c] mt-4 leading-relaxed">Up Smart Learning is a UK tutoring service built around one belief: with the right teaching, every student can achieve their target grade. We specialise exclusively in GCSE and A-Level exam preparation, so everything we do is focused on results.</p>
            <div className="mt-6 space-y-4">
              {[
                ["Scientific teaching & learning methods", "We use evidence-based techniques — spaced repetition, retrieval practice and active recall — not just re-reading notes."],
                ["Examiner-led expertise", "Many of our tutors are practising GCSE and A-Level examiners who know exactly what earns marks."],
                ["Genuinely personalised", "Diagnostic-led plans tailored to each student's exam board and weakest topics."],
              ].map(([h, d]) => (
                <div key={h} className="flex gap-3">
                  <span className="mt-1 h-6 w-6 shrink-0 rounded-full bg-[#595C2B]/10 flex items-center justify-center"><Check className="h-3.5 w-3.5 text-[#595C2B]" /></span>
                  <div><p className="font-semibold text-[#3a3f2e]">{h}</p><p className="text-sm text-[#6a6f58] mt-0.5">{d}</p></div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 px-6 bg-[#CCCFC7]">
        <div className="max-w-7xl mx-auto">
          <motion.h2 {...fade()} className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#2A1E0E]">Three steps to booked</motion.h2>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {[
              { i: CalendarCheck, t: "Choose & schedule", d: "Pick your child's subject, level and a specialist tutor, then select a time that suits your family." },
              { i: ShieldCheck, t: "Pay securely", d: "Confirm your booking with secure card payment. Single lessons, small groups or discounted packages." },
              { i: Video, t: "Learn on Google Meet", d: "A Google Meet link is generated automatically. Join with one click from your dashboard." },
            ].map((s, i) => (
              <motion.div key={s.t} {...fade(i * 0.1)} className="rounded-2xl bg-[#595C2B] text-white p-8">
                <div className="h-12 w-12 rounded-xl bg-[#BA8A30] flex items-center justify-center"><s.i className="h-6 w-6 text-[#2A1E0E]" /></div>
                <h3 className="font-display text-2xl font-semibold mt-5">{s.t}</h3>
                <p className="text-white/75 mt-2 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Courses & Packages */}
      <section id="courses" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fade()} className="rounded-3xl bg-[#BA8A30] text-[#2A1E0E] p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold">Limited availability</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2">Grade Booster Crash Courses</h2>
              <p className="mt-2 max-w-xl">Intensive small-group revision over the school holidays — cover a full topic and exam technique in focused sessions before the exams.</p>
            </div>
            <Link to="/login?mode=register"><Button size="lg" className="rounded-full bg-[#2A1E0E] hover:bg-[#241d08] text-white btn-lift h-12 px-8" data-testid="crash-course-btn">Reserve a place</Button></Link>
          </motion.div>

          <motion.div {...fade(0.1)} className="mt-14">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#BA8A30]">Packages</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#595C2B] mt-3 tracking-tight">Hour packages with free lessons included</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {packages.map((p, i) => (
              <motion.div key={p.t} {...fade(i * 0.08)} className={`rounded-2xl border p-8 ${p.featured ? "border-[#BA8A30] bg-[#595C2B] text-white" : "border-[#E7E2D3] bg-white"}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-2xl font-semibold">{p.t}</h3>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${p.featured ? "bg-[#BA8A30] text-[#2A1E0E]" : "bg-[#595C2B]/10 text-[#595C2B]"}`}>{p.free}</span>
                </div>
                <div className="mt-5 flex items-end gap-4">
                  <div><p className={`text-xs uppercase tracking-wide ${p.featured ? "text-white/60" : "text-[#8a8f78]"}`}>GCSE</p><p className={`text-3xl font-bold ${p.featured ? "text-[#BA8A30]" : "text-[#595C2B]"}`}>{p.gcse}</p></div>
                  <div><p className={`text-xs uppercase tracking-wide ${p.featured ? "text-white/60" : "text-[#8a8f78]"}`}>A-Level</p><p className={`text-3xl font-bold ${p.featured ? "text-[#BA8A30]" : "text-[#595C2B]"}`}>{p.alevel}</p></div>
                </div>
                <ul className="mt-6 space-y-2">
                  {p.pts.map((x) => <li key={x} className={`flex items-center gap-2 text-sm ${p.featured ? "text-white/80" : "text-[#4a4f3c]"}`}><Check className={`h-4 w-4 ${p.featured ? "text-[#BA8A30]" : "text-[#595C2B]"}`} />{x}</li>)}
                </ul>
                <Link to="/login?mode=register"><Button className={`mt-8 w-full rounded-full btn-lift ${p.featured ? "bg-[#BA8A30] hover:bg-[#9C7328] text-[#2A1E0E]" : "bg-[#595C2B] hover:bg-[#45481F] text-white"}`} data-testid={`package-${p.t.split("-")[0]}`}>Book this package</Button></Link>
              </motion.div>
            ))}
          </div>
          <motion.div {...fade(0.1)} className="mt-14 rounded-3xl border border-[#d8cfb8] bg-white p-8 md:p-10" data-testid="referral-section">
            <div className="grid md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-2">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#BA8A30]">Referral scheme</p>
                <h3 className="font-display text-3xl font-bold text-[#595C2B] mt-2">Refer a friend, receive £25</h3>
                <p className="text-[#45481F] mt-3 leading-relaxed">Love learning with Up Smart? Refer a friend and receive <b>£25 via bank transfer</b> once they get started. There's no limit — refer as many friends as you like.</p>
              </div>
              <div className="rounded-2xl bg-[#595C2B] text-white p-6 text-center">
                <p className="font-display text-4xl font-bold text-[#BA8A30]">£25</p>
                <p className="text-white/80 text-sm mt-1">per successful referral, paid by bank transfer</p>
                <Link to="/login?mode=register"><Button className="mt-4 w-full rounded-full bg-[#BA8A30] hover:bg-[#9C7328] text-[#2A1E0E] font-semibold btn-lift" data-testid="referral-cta">Get started</Button></Link>
              </div>
            </div>
            <details className="mt-6 group rounded-xl bg-[#EFE8D8] p-4" data-testid="referral-terms">
              <summary className="cursor-pointer list-none flex items-center gap-2 text-sm font-semibold text-[#595C2B]">
                <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" /> Referral scheme terms &amp; conditions
              </summary>
              <div className="mt-3 text-sm text-[#6a6f58] leading-relaxed space-y-2">
                <p>To qualify for the £25 referral reward, the referred student must complete a minimum of <b>4 paid lessons</b> before the referrer can claim the offer.</p>
                <p>The £25 reward is paid via bank transfer once the qualifying lessons are completed. Rewards are given per successfully referred student. Package "free" lessons do not count towards the 4-lesson requirement. Up Smart Learning reserves the right to amend or withdraw this scheme at any time.</p>
              </div>
            </details>
          </motion.div>
        </div>
      </section>

      {/* Prices (single/group) */}
      <section id="prices" className="py-24 px-6 bg-white/60">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fade()}>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#BA8A30]">Prices</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#595C2B] mt-3 tracking-tight">Single &amp; small-group lessons</h2>
            <p className="text-[#6a6f58] mt-2">Groups of 2–4 students. Pay as you go, no contracts.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-6 mt-10">
            {[
              { t: "GCSE", single: "£25", group: "£20" },
              { t: "A-Level", single: "£28", group: "£22" },
            ].map((row, i) => (
              <motion.div key={row.t} {...fade(i * 0.08)} className="rounded-2xl border border-[#E7E2D3] bg-white p-8">
                <h3 className="font-display text-2xl font-semibold text-[#595C2B]">{row.t}</h3>
                <div className="mt-6 grid grid-cols-2 gap-4">
                  <div className="rounded-xl bg-[#EFE8D8] p-5"><p className="text-xs uppercase tracking-wide text-[#8a8f78]">Single (1-to-1)</p><p className="text-3xl font-bold text-[#595C2B] mt-1">{row.single}<span className="text-sm font-medium text-[#8a8f78]">/hr</span></p></div>
                  <div className="rounded-xl bg-[#EFE8D8] p-5"><p className="text-xs uppercase tracking-wide text-[#8a8f78]">Group (2–4)</p><p className="text-3xl font-bold text-[#595C2B] mt-1">{row.group}<span className="text-sm font-medium text-[#8a8f78]">/hr</span></p></div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/login?mode=register" data-testid="prices-book-btn">
              <Button size="lg" className="rounded-full bg-[#BA8A30] hover:bg-[#9C7328] text-[#2A1E0E] font-semibold btn-lift h-12 px-8">Book a lesson</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Exam Boards */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#BA8A30]">Exam boards we cover</p>
          <div className="flex flex-wrap items-center justify-center gap-4 mt-6">
            {examBoards.map((b) => (
              <span key={b} className="rounded-full border border-[#E7E2D3] bg-white px-6 py-2.5 font-display text-lg font-semibold text-[#595C2B]" data-testid={`board-${b}`}>{b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Team */}
      <section id="team" className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fade()}>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#BA8A30]">Meet the team</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#595C2B] mt-3 tracking-tight">Experienced, Dedicated, and Results-Driven</h2>
            <p className="text-[#45481F] mt-4 leading-relaxed">Our tutors are hand-picked educators who combine subject mastery with a genuine passion for helping students succeed. Every tutor is DBS-checked and exam-board trained.</p>
          </motion.div>
          <div className="mt-8 space-y-5">
            {[
              ["Subject Specialists", "High-achieving educators with deep curriculum and exam-board expertise."],
              ["Personalized Approach", "Customized lesson plans tailored to your child's specific goals, pace, and needs."],
              ["Mentorship Beyond the Classroom", "Helping students master complex topics, refine exam techniques, and build long-term confidence."],
            ].map(([h, d], i) => (
              <motion.div key={h} {...fade(i * 0.08)} className="flex gap-4 rounded-2xl border border-[#d8cfb8] bg-white p-6" data-testid={`team-point-${i}`}>
                <span className="mt-1 h-8 w-8 shrink-0 rounded-full bg-[#595C2B]/10 flex items-center justify-center"><Check className="h-4 w-4 text-[#595C2B]" /></span>
                <div><p className="font-display text-lg font-semibold text-[#595C2B]">{h}</p><p className="text-[#45481F] mt-1 leading-relaxed">{d}</p></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning methods */}
      <section id="methods" className="py-24 px-6 bg-[#595C2B] text-white">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fade()}>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#BA8A30]">Learning tools &amp; methods</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3 tracking-tight">Scientific teaching that gets results</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {methods.map((m, i) => (
              <motion.div key={m.t} {...fade(i * 0.1)} className="rounded-2xl border border-white/10 bg-white/5 p-8">
                <div className="h-12 w-12 rounded-xl bg-[#BA8A30] flex items-center justify-center"><m.icon className="h-6 w-6 text-[#2A1E0E]" /></div>
                <h3 className="font-display text-2xl font-semibold mt-5">{m.t}</h3>
                <p className="text-white/70 mt-2 leading-relaxed">{m.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Information for parents */}
      <section id="parents" className="py-24 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-14 items-center">
          <motion.div {...fade()}>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#BA8A30]">Information for parents</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#595C2B] mt-3 tracking-tight">Everything you need, all in one dashboard</h2>
            <ul className="mt-6 space-y-3">
              {[
                "Add each of your children and their study level",
                "Book single, group or package lessons in minutes",
                "Secure card payments and a full payment history",
                "Automatic lesson-reminder emails the day before",
                "One-click Google Meet links for every session",
              ].map((x) => (
                <li key={x} className="flex items-center gap-3 text-[#4a4f3c]"><span className="h-6 w-6 rounded-full bg-[#595C2B]/10 flex items-center justify-center"><Check className="h-3.5 w-3.5 text-[#595C2B]" /></span>{x}</li>
              ))}
            </ul>
            <Link to="/login?mode=register"><Button className="mt-8 rounded-full bg-[#595C2B] hover:bg-[#45481F] text-white btn-lift" data-testid="parents-cta">Create a parent account</Button></Link>
          </motion.div>
          <motion.img {...fade(0.1)} src="https://images.pexels.com/photos/5905709/pexels-photo-5905709.jpeg" alt="Student on a video call with a tutor" className="rounded-2xl object-cover h-[420px] w-full shadow-lg" />
        </div>
      </section>

      {/* Reviews */}
      <section id="reviews" className="py-24 px-6 bg-white/60">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fade()}><p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#BA8A30]">Reviews</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#595C2B] mt-3 tracking-tight">Loved by UK families</h2></motion.div>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {reviews.map((rv, i) => (
              <motion.div key={rv.n} {...fade(i * 0.08)} className="rounded-2xl border border-[#E7E2D3] bg-white p-7">
                <div className="flex gap-1">{[...Array(rv.stars)].map((_, k) => <Star key={k} className="h-4 w-4 fill-[#BA8A30] text-[#BA8A30]" />)}</div>
                <p className="text-[#4a4f3c] mt-4 leading-relaxed">“{rv.r}”</p>
                <p className="text-sm font-semibold text-[#595C2B] mt-4">{rv.n} <span className="text-[#8a8f78] font-normal">· {rv.role}</span></p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* News & events */}
      <section id="news" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fade()} className="flex items-center gap-3">
            <Newspaper className="h-6 w-6 text-[#BA8A30]" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#595C2B] tracking-tight">News &amp; events</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {[
              { d: "School Holidays", t: "GCSE Maths & Science crash courses", x: "Intensive small-group revision across the school holidays — limited places available." },
              { d: "New for 2026", t: "Psychology added as an A-Level science", x: "We now offer specialist AQA & Edexcel Psychology tuition at GCSE and A-Level." },
              { d: "For students", t: "Study Skills & Wellbeing Webinar", x: "A free live webinar helping students build effective study habits, manage exam stress and stay motivated." },
            ].map((n, i) => (
              <motion.div key={n.t} {...fade(i * 0.08)} className="rounded-2xl border border-[#E7E2D3] bg-white p-7">
                <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#BA8A30]">{n.d}</p>
                <h3 className="font-display text-xl font-semibold text-[#595C2B] mt-2">{n.t}</h3>
                <p className="text-sm text-[#6a6f58] mt-2 leading-relaxed">{n.x}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-24 px-6">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-start">
          <motion.div {...fade()}>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#BA8A30]">Contact</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#595C2B] mt-3 tracking-tight">Talk to our team</h2>
            <p className="text-[#4a4f3c] mt-4 leading-relaxed">Not sure which subject or package is right for your child? Please book to arrange a free meeting where we'll discuss a personalised learning plan and help you get started.</p>
            <div className="mt-6 space-y-2 text-[#4a4f3c]">
              <p className="flex items-center gap-2"><Users className="h-4 w-4 text-[#595C2B]" /> UK-based tutoring team</p>
              <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#595C2B]" /> DBS-checked specialist tutors</p>
            </div>
          </motion.div>
          <motion.div {...fade(0.1)}><Contact /></motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#595C2B] text-white/70 pt-14 pb-8 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2">
              <img src="/logo_mark.png" alt="Up Smart Learning" className="h-9 w-9 object-contain" />
              <span className="font-display text-lg font-bold text-white">Up Smart Learning</span>
            </div>
            <p className="text-sm mt-4 max-w-sm">Specialist UK tutoring for GCSE &amp; A-Level exam success. Expert tutors, scientific learning methods, live on Google Meet.</p>
            <div className="mt-5 space-y-2 text-sm">
              <a href="mailto:info@upsmartlearning.co.uk" className="flex items-center gap-2 hover:text-white" data-testid="footer-email"><Mail className="h-4 w-4 text-[#BA8A30]" /> info@upsmartlearning.co.uk</a>
              <a href="https://www.facebook.com/" target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white" data-testid="footer-facebook"><Facebook className="h-4 w-4 text-[#BA8A30]" /> Up Smart Learning</a>
              <a href="https://www.instagram.com/up_smart_learning" target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white" data-testid="footer-instagram"><Instagram className="h-4 w-4 text-[#BA8A30]" /> @UP_SMART_LEARNING</a>
            </div>
          </div>
          <div>
            <p className="text-white font-semibold mb-3">Explore</p>
            <ul className="space-y-2 text-sm">
              <li><a href="#subjects" className="hover:text-white">Subjects</a></li>
              <li><a href="#courses" className="hover:text-white">Courses &amp; Packages</a></li>
              <li><a href="#prices" className="hover:text-white">Prices</a></li>
              <li><a href="#team" className="hover:text-white">Meet the Team</a></li>
              <li><a href="#reviews" className="hover:text-white">Reviews</a></li>
            </ul>
          </div>
          <div>
            <p className="text-white font-semibold mb-3">Company</p>
            <ul className="space-y-2 text-sm">
              <li><a href="#about" className="hover:text-white">About Us</a></li>
              <li><a href="#contact" className="hover:text-white">Contact</a></li>
              <li><Link to="/legal/terms" className="hover:text-white" data-testid="footer-terms">Terms &amp; Conditions</Link></li>
              <li><Link to="/legal/privacy" className="hover:text-white" data-testid="footer-privacy">Privacy Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-sm">
          <p>© {new Date().getFullYear()} Up Smart Learning Ltd · UK Tutoring</p>
          <p>Exam boards: {examBoards.join(" · ")}</p>
        </div>
      </footer>
    </div>
  );
}
